import { useEffect, useRef, useState } from 'react';

export interface AudioContextState {
  audioContext: AudioContext | null;
  analyserNode: AnalyserNode | null;
  isPlaying: boolean;
  currentFrequency: number;
  playFrequency: (frequency: number) => void;
  stopFrequency: () => void;
  setVolume: (volume: number) => void;
}

export const useAudioContext = (): AudioContextState => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const oscillatorRef = useRef<OscillatorNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const analyserNodeRef = useRef<AnalyserNode | null>(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentFrequency, setCurrentFrequency] = useState(0);
  const [volume, setVolumeState] = useState(0.3);

  useEffect(() => {
    // Initialize AudioContext on first user interaction
    const initAudioContext = () => {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
    };

    // Add event listener for user interaction
    document.addEventListener('click', initAudioContext, { once: true });
    document.addEventListener('touchstart', initAudioContext, { once: true });

    return () => {
      document.removeEventListener('click', initAudioContext);
      document.removeEventListener('touchstart', initAudioContext);
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const playFrequency = (frequency: number) => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    // Stop any existing oscillator
    stopFrequency();

    const audioContext = audioContextRef.current;
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    // Create analyser if it doesn't exist
    if (!analyserNodeRef.current) {
      analyserNodeRef.current = audioContext.createAnalyser();
      analyserNodeRef.current.connect(audioContext.destination);
    }
    const analyser = analyserNodeRef.current;

    oscillator.connect(gainNode);
    gainNode.connect(analyser);

    oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
    oscillator.type = 'sine'; // Always use sine wave
    
    // Smooth volume ramp to avoid clicks
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + 0.05);

    oscillatorRef.current = oscillator;
    gainNodeRef.current = gainNode;

    oscillator.start();
    setIsPlaying(true);
    setCurrentFrequency(frequency);
  };

  const stopFrequency = () => {
    if (oscillatorRef.current && gainNodeRef.current && audioContextRef.current) {
      const audioContext = audioContextRef.current;
      
      // Smooth volume ramp down to avoid clicks
      gainNodeRef.current.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.05);
      
      setTimeout(() => {
        if (oscillatorRef.current) {
          oscillatorRef.current.stop();
          oscillatorRef.current.disconnect();
          oscillatorRef.current = null;
        }
        if (gainNodeRef.current) {
          gainNodeRef.current.disconnect();
          gainNodeRef.current = null;
        }
      }, 50);
    }
    setIsPlaying(false);
    setCurrentFrequency(0);
  };

  const setVolume = (newVolume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolumeState(clampedVolume);
    
    if (gainNodeRef.current && audioContextRef.current) {
      gainNodeRef.current.gain.linearRampToValueAtTime(
        clampedVolume,
        audioContextRef.current.currentTime + 0.05
      );
    }
  };


  return {
    audioContext: audioContextRef.current,
    analyserNode: analyserNodeRef.current,
    isPlaying,
    currentFrequency,
    playFrequency,
    stopFrequency,
    setVolume,
  };
};